/**
 * Hallyu4U API Store
 */

(function() {
  'use strict';

  function createStore(initializer) {
    let state = {};
    const listeners = new Set();

    const setState = (partial) => {
      const nextState = typeof partial === 'function' ? partial(state) : partial;
      if (nextState !== state) {
        const previousState = state;
        state = { ...state, ...nextState };
        listeners.forEach((listener) => listener(state, previousState));
      }
    };

    const getState = () => state;

    const subscribe = (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    };

    const store = initializer(setState, getState, { getState, setState, subscribe });
    
    // Store의 메서드들을 직접 노출
    return Object.assign(store, {
      getState,
      setState,
      subscribe
    });
  }

  /**
   * API Base URL 결정
   */
  const getApiBaseUrl = () => {
    // 1. window.API_BASE_URL이 설정되어 있으면 우선 사용
    if (window.API_BASE_URL) {
      return window.API_BASE_URL;
    }
    
    // 2. window.HALLYU4U_CONFIG에서 가져오기
    if (window.HALLYU4U_CONFIG?.apiUrl) {
      return window.HALLYU4U_CONFIG.apiUrl;
    }
    
    // 3. 환경에 따라 자동 결정
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8080';
    }
    
    return 'https://hallyu4u.api.hallyu-manager.com';
  };

  /**
   * 인증 토큰 가져오기
   */
  const getAuthToken = () => {
    return localStorage.getItem('h4u_token') || 
            sessionStorage.getItem('h4u_token') || 
            localStorage.getItem('customerAccessToken') ||
            null;
  };

  /**
   * JWT의 exp 클레임으로 토큰 유효성 로컬 검증
   * (만료 5분 전부터는 무효로 간주하여 미리 재발급)
   */
  const isTokenValidLocally = (token) => {
    if (!token) return false;

    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) return false;

      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));

      if (!payload.exp) return false;

      const TIME_BUFFER_MS = 5 * 60 * 1000;
      return (payload.exp * 1000 - TIME_BUFFER_MS) > Date.now();
    } catch (error) {
      return false;
    }
  };

  /**
   * 저장된 인증 정보 전부 삭제
   */
  const clearAuthStorage = () => {
    try {
      localStorage.removeItem('h4u_token');
      localStorage.removeItem('h4u_refresh_token');
      localStorage.removeItem('customerAccessToken');
      sessionStorage.removeItem('h4u_token');
    } catch (error) {
      console.error('[API Store] Failed to clear auth storage:', error);
    }
  };

  /**
   * 강제 로그아웃
   * - h4u 토큰/세션 삭제
   * - Shopify 세션도 함께 종료 (routes.account_logout_url 로 이동)
   *
   * /auth (재인증)까지 실패한 뒤에만 호출해야 한다.
   * 토큰이 아직 없어서 발생하는 403(로그인 직후 등)에는 호출하면 안 된다.
   */
  let isLoggingOut = false;
  const forceLogout = (reason = 'unknown') => {
    if (isLoggingOut) return;
    isLoggingOut = true;

    console.warn('[API Store] Force logout triggered:', reason);

    clearAuthStorage();

    try {
      window.dispatchEvent(new CustomEvent('hallyu4u:force-logout', { detail: { reason } }));
    } catch (error) {
      // CustomEvent 미지원 환경 무시
    }

    // Shopify 로그인 상태가 아니면 끊을 세션이 없다. 임의 이동시키지 않는다.
    if (window.H4U_IS_LOGGED_IN === false) {
      console.warn('[API Store] Shopify 로그인 상태가 아니므로 리다이렉트 없이 토큰만 삭제');
      isLoggingOut = false;
      return;
    }

    window.location.href = window.H4U_LOGOUT_URL || '/account/logout';
  };

  // 다른 스크립트(Liquid 인라인 포함)에서도 사용할 수 있도록 전역 노출
  window.hallyu4uForceLogout = forceLogout;

  /**
   * 기본 헤더 생성
   */
  const getDefaultHeaders = (token = null) => {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    const authToken = token || getAuthToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Shopify 관련 헤더
    if (window.customerId) {
      headers['X-Customer-Id'] = window.customerId;
    }
    if (window.customerEmail) {
      headers['X-Customer-Email'] = window.customerEmail;
    }
    if (window.Shopify?.shop) {
      headers['X-Shopify-Domain'] = window.Shopify.shop;
    }
    if (localStorage.getItem('customerAccessToken')) {
      headers['X-Shopify-Customer-Access-Token'] = localStorage.getItem('customerAccessToken');
    }

    return headers;
  };

  /**
   * API Store 생성
   */
  let useApiStore;

  // /auth 재인증 in-flight 프로미스 (중복 호출 방지)
  let refreshPromise = null;

  // get 함수가 store 자체를 반환하도록 클로저 사용
  useApiStore = createStore((set, getState) => {
    // get 함수는 store 자체를 반환
    const get = () => useApiStore;
    
    return {
      // 상태
      loading: {},
      errors: {},
      lastRequest: null,

      /**
       * 토큰 갱신 함수
       * /auth API를 호출하여 새 토큰을 받아옴
       *
       * 동시에 여러 곳(레이아웃 부트스트랩 + 대시보드 등)에서 호출되어도
       * /auth 는 한 번만 나가도록 in-flight 프로미스를 공유한다.
       */
      refreshToken: async () => {
        if (refreshPromise) {
          return await refreshPromise;
        }

        const pending = (async () => {
          try {
            // /auth API 호출 (Shopify 고객 인증)
            const authResult = await axios({
              method: 'POST',
              url: `${getApiBaseUrl()}/api/auth`,
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...(window.customerId ? { 'X-Customer-Id': window.customerId } : {}),
                ...(window.customerEmail ? { 'X-Customer-Email': window.customerEmail } : {}),
                ...(window.Shopify?.shop ? { 'X-Shopify-Domain': window.Shopify.shop } : {}),
                ...(localStorage.getItem('customerAccessToken') ? { 'X-Shopify-Customer-Access-Token': localStorage.getItem('customerAccessToken') } : {})
              },
              data: {
                customerId: window.customerId
              },
              timeout: 30000,
              withCredentials: true
            });

            // 토큰 추출 및 저장
            if (authResult.data) {
              let token = null;
            
              // 응답 형식에 따라 토큰 추출
              if (typeof authResult.data === 'string') {
                token = authResult.data;
              } else if (authResult.data.data) {
                token = authResult.data.data;
              } else if (authResult.data.token) {
                token = authResult.data.token;
              }

              if (token) {
                localStorage.setItem('h4u_token', token);
                console.log('[API Store] Token refreshed successfully');
                return token;
              }
            }

            console.warn('[API Store] Token refresh failed: No token in response');
            return null;
          } catch (error) {
            console.error('[API Store] Token refresh error:', error);
            return null;
          }
        })();

        refreshPromise = pending;

        try {
          return await pending;
        } finally {
          refreshPromise = null;
        }
      },

      /**
       * 유효한 토큰 확보
       * - 로컬 토큰이 아직 유효하면 그대로 사용
       * - 없거나 만료(임박)면 /auth 재인증 (동시 호출은 하나로 합쳐짐)
       *
       * Shopify 로그인 직후처럼 아직 h4u 토큰이 없는 상태에서
       * /auth/me 를 먼저 때려 403이 나는 것을 막기 위한 진입점.
       */
      ensureToken: async (options = {}) => {
        const force = options.force || false;
        const token = getAuthToken();

        if (!force && isTokenValidLocally(token)) {
          return token;
        }

        // Shopify 로그인 상태가 아니면 /auth 로 발급받을 수 없다
        if (window.H4U_IS_LOGGED_IN === false || !window.customerId) {
          return null;
        }

        return await useApiStore.refreshToken();
      },

      /**
       * 공통 API 요청 함수
       * 모든 API 호출의 기반이 되는 함수
       * 401 에러 발생 시 자동으로 토큰 갱신 후 재시도
       */
      request: async (endpoint, options = {}) => {
        const requestKey = options.key || endpoint;
        const isRetry = options._isRetry || false; // 재시도 플래그
        
        // 로딩 시작
        set((state) => ({
          loading: { ...state.loading, [requestKey]: true },
          errors: { ...state.errors, [requestKey]: null }
        }));

        const baseUrl = getApiBaseUrl();
        if (!baseUrl) {
          const error = 'API base URL not configured';
          set((state) => ({
            loading: { ...state.loading, [requestKey]: false },
            errors: { ...state.errors, [requestKey]: error }
          }));
          return { success: false, error, data: null };
        }

        // 외부 URL인지 확인 (http:// 또는 https://로 시작)
        const isExternalUrl = endpoint.startsWith('http://') || endpoint.startsWith('https://');
        
        let url;
        if (isExternalUrl) {
          // 외부 URL인 경우 그대로 사용
          url = endpoint;
        } else {
          // 내부 API인 경우 baseUrl 사용
          const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
          const apiEndpoint = normalizedEndpoint.startsWith('/api/') 
            ? normalizedEndpoint 
            : `/api${normalizedEndpoint}`;
          url = `${baseUrl}${apiEndpoint}`;
        }

        // Axios 설정
        // 외부 URL인 경우 기본 헤더를 적용하지 않고, options.headers만 사용
        // 내부 API인 경우 기본 헤더를 적용하고 options.headers로 덮어쓰기
        const axiosConfig = {
          method: options.method || 'GET',
          url: url,
          headers: isExternalUrl
            ? { ...options.headers }
            : {
                ...getDefaultHeaders(options.token),
                ...options.headers
              },
          timeout: options.timeout || 30000,
          withCredentials: options.withCredentials !== false
        };

        if (options.body || options.data) {
          axiosConfig.data = options.body || options.data;
        }

        if (options.params) {
          axiosConfig.params = options.params;
        }

        try {
          const response = await axios(axiosConfig);
          
          // 로딩 종료
          set((state) => ({
            loading: { ...state.loading, [requestKey]: false },
            lastRequest: { endpoint, options, response: response.data, timestamp: Date.now() }
          }));

          // 응답 형식 통일
          return {
            success: true,
            data: response.data,
            response: response,
            status: response.status
          };
        } catch (error) {
          const errorMessage = error.response 
            ? `HTTP ${error.response.status}: ${error.response.statusText || error.message}`
            : error.message;

          // 403 Forbidden 처리 (/auth/me 한정)
          // 이 API는 토큰이 없거나 유효하지 않을 때도 403을 반환한다.
          // Shopify 로그인 직후에는 아직 h4u 토큰이 없어 정상적으로 403이 날 수 있으므로,
          // 먼저 /auth 재인증 후 1회 재시도하고 그래도 403이면 그때 강제 로그아웃한다.
          if (error.response?.status === 403 && endpoint.includes('/auth/me')) {
            if (!isRetry) {
              console.log('[API Store] /auth/me 403 detected, re-authenticating...');
              const newToken = await useApiStore.ensureToken({ force: true });

              if (newToken) {
                return await useApiStore.request(endpoint, {
                  ...options,
                  token: newToken,
                  _isRetry: true
                });
              }
            }

            set((state) => ({
              loading: { ...state.loading, [requestKey]: false },
              errors: { ...state.errors, [requestKey]: errorMessage },
              lastRequest: { endpoint, options, error: errorMessage, timestamp: Date.now() }
            }));

            // 재인증까지 했는데도 403 -> 이 계정으로는 더 이상 접근 불가
            forceLogout('/auth/me returned 403 after re-auth');

            return {
              success: false,
              error: errorMessage,
              data: null,
              status: 403
            };
          }

          // 401 Unauthorized 처리
          if (error.response?.status === 401) {
            // /auth 엔드포인트 자체는 재시도하지 않음 (무한 루프 방지)
            if (endpoint.includes('/auth') && !endpoint.includes('/auth/me')) {
              localStorage.removeItem('h4u_token');
              sessionStorage.removeItem('h4u_token');
              window.dispatchEvent(new CustomEvent('hallyu4u:unauthorized'));
              
              set((state) => ({
                loading: { ...state.loading, [requestKey]: false },
                errors: { ...state.errors, [requestKey]: errorMessage },
                lastRequest: { endpoint, options, error: errorMessage, timestamp: Date.now() }
              }));

              return {
                success: false,
                error: errorMessage,
                data: null,
                status: 401
              };
            }

            // 이미 재시도한 경우에는 다시 재시도하지 않음
            if (isRetry) {
              localStorage.removeItem('h4u_token');
              sessionStorage.removeItem('h4u_token');
              window.dispatchEvent(new CustomEvent('hallyu4u:unauthorized'));
              
              set((state) => ({
                loading: { ...state.loading, [requestKey]: false },
                errors: { ...state.errors, [requestKey]: errorMessage },
                lastRequest: { endpoint, options, error: errorMessage, timestamp: Date.now() }
              }));

              return {
                success: false,
                error: errorMessage,
                data: null,
                status: 401
              };
            }

            // 토큰 갱신 시도
            console.log('[API Store] 401 error detected, attempting token refresh...');
            const newToken = await useApiStore.refreshToken();

            if (newToken) {
              // 새 토큰으로 원래 요청 재시도
              console.log('[API Store] Retrying request with new token...');
              return await useApiStore.request(endpoint, {
                ...options,
                token: newToken,
                _isRetry: true // 재시도 플래그 설정
              });
            } else {
              // 토큰 갱신 실패
              localStorage.removeItem('h4u_token');
              sessionStorage.removeItem('h4u_token');
              window.dispatchEvent(new CustomEvent('hallyu4u:unauthorized'));
            }
          }

          // 로딩 종료 및 에러 저장
          set((state) => ({
            loading: { ...state.loading, [requestKey]: false },
            errors: { ...state.errors, [requestKey]: errorMessage },
            lastRequest: { endpoint, options, error: errorMessage, timestamp: Date.now() }
          }));

          return {
            success: false,
            error: errorMessage,
            data: null,
            status: error.response?.status || null
          };
        }
      },

      // ============================================
      // 인증 API
      // ============================================
      auth: {
      /**
       * 유효한 토큰 확보 (store 레벨 ensureToken 위임)
       * 호출부는 Hallyu4UAPI.auth.ensureToken() 형태를 사용한다.
       */
      ensureToken: async (options) => await useApiStore.ensureToken(options),

      /**
       * 토큰 설정 (수동으로 토큰을 설정할 때 사용)
       */
      setToken: (token) => {
        if (token) {
          localStorage.setItem('h4u_token', token);
          console.log('[API Store] Token set manually');
        } else {
          localStorage.removeItem('h4u_token');
          sessionStorage.removeItem('h4u_token');
        }
      },

      /**
       * 로그인
       */
      login: async (credentials) => {
        const result = await useApiStore.request('/auth/login', {
          method: 'POST',
          body: credentials,
          key: 'auth.login'
        });

        // 성공 시 토큰 저장
        if (result.success && result.data?.token) {
          localStorage.setItem('h4u_token', result.data.token);
          if (result.data.refreshToken) {
            localStorage.setItem('h4u_refresh_token', result.data.refreshToken);
          }
        }

        return result;
      },

      /**
       * 로그아웃
       */
      logout: async () => {
        // await useApiStore.request('/auth/logout', {
        //   method: 'POST',
        //   key: 'auth.logout'
        // });

        // 토큰 제거
        clearAuthStorage();
      },

      /**
       * 강제 로그아웃 (h4u 토큰 삭제 + Shopify 로그아웃 리다이렉트)
       */
      forceLogout: (reason) => forceLogout(reason || 'manual'),

      /**
       * 현재 사용자 정보 조회
       */
      getMe: async () => {
        return await useApiStore.request('/auth/me', {
          method: 'GET',
          key: 'auth.me'
        });
      },

      /**
       * Shopify 고객 인증
       */
      authenticateShopifyCustomer: async () => {
        return await useApiStore.request('/auth', {
          method: 'POST',
          body: {
            customerId: window.customerId
          },
          key: 'auth.shopify'
        });
      }
    },

      // ============================================
      // 주문 API
      // ============================================
      orders: {
      /**
       * 주문 목록 조회
       */
      getList: async (params = {}) => {
        const { isExpired = null, orderType = null, orderStatus = null, page = 0, size = 10, keyword = null } = params;
        
        const endpoint = `/orders/all?page=${page}&size=${size}`;
        const body = {};
        if (isExpired !== null && isExpired !== undefined) body.isExpired = isExpired;
        if (orderType !== null && orderType !== undefined) body.orderType = orderType;
        if (orderStatus !== null && orderStatus !== undefined) body.orderStatus = orderStatus;
        if (keyword && String(keyword).trim()) body.keyword = String(keyword).trim();

        return await useApiStore.request(endpoint, {
          method: 'POST',
          body: Object.keys(body).length > 0 ? body : undefined,
          key: 'orders.list'
        });
      },

      /**
       * 주문 상세 조회
       */
      getDetails: async (submissionId) => {
        return await useApiStore.request(`/orders/${submissionId}`, {
          method: 'GET',
          key: `orders.details.${submissionId}`
        });
      },

      /**
       * We Buy We Ship 주문 제출
       */
      submitWeBuy: async (formData) => {
        return await useApiStore.request('/orders', {
          method: 'POST',
          body: formData,
          key: 'orders.submitWeBuy'
        });
      },

      /**
       * You Buy We Ship 주문 제출
       */
      submitYouBuy: async (formData) => {
        return await useApiStore.request('/orders', {
          method: 'POST',
          body: formData,
          key: 'orders.submitYouBuy'
        });
      },

      /**
       * 주문 상태 조회
       */
      getStatus: async (orderId) => {
        return await useApiStore.request(`/orders/${orderId}/status`, {
          method: 'GET',
          key: `orders.status.${orderId}`
        });
      }
    },

      // ============================================
      // 패키지 API
      // ============================================
      packages: {
      /**
       * 패키지 목록 조회
       */
      getList: async (customerId, params = {}) => {
        const queryParams = new URLSearchParams({
          customerId,
          ...params
        });
        return await useApiStore.request(`/packages?${queryParams}`, {
          method: 'GET',
          key: 'packages.list'
        });
      },

      /**
       * 패키지 상세 조회
       */
      getDetails: async (packageId) => {
        return await useApiStore.request(`/packages/${packageId}`, {
          method: 'GET',
          key: `packages.details.${packageId}`
        });
      },

      /**
       * 패키지 추적
       */
      track: async (trackingNumber) => {
        return await useApiStore.request('/packages/track', {
          method: 'POST',
          body: { trackingNumber },
          key: 'packages.track'
        });
      },

      /**
       * 창고 패키지 목록
       */
      getWarehousePackages: async (params = {}) => {
        const store = get();
        const { page = 0, size = 10 } = params;
        return await useApiStore.request(`/orders/in-warehouse/all?page=${page}&size=${size}`, {
          method: 'POST',
          body: { orderStatus: 'IN_WAREHOUSE', orderType: 'ALL' },
          key: 'packages.warehouse'
        });
      },

      /**
       * 창고 패키지 상세
       */
      getWarehousePackageDetails: async (packageId, customerEmail, warehouseId) => {
        return await useApiStore.request(`/packages/warehouse/${packageId}`, {
          method: 'POST',
          body: { customerEmail, warehouseId },
          key: `packages.warehouse.details.${packageId}`
        });
      },

      /**
       * 패키지 통합 요청
       */
      consolidate: async (packageIds, options = {}) => {
        return await useApiStore.request('/packages/consolidate', {
          method: 'POST',
          body: {
            customerEmail: window.customerEmail,
            warehouseId: options.warehouseId,
            packageIds,
            ...options
          },
          key: 'packages.consolidate'
        });
      },

      /**
       * 패키지 사진 요청
       */
      requestPhotos: async (packageIds, options = {}) => {
        return await useApiStore.request('/packages/photos', {
          method: 'POST',
          body: {
            customerEmail: window.customerEmail,
            warehouseId: options.warehouseId,
            packageIds,
            ...options
          },
          key: 'packages.photos'
        });
      }
    },

      // ============================================
      // 배송 API
      // ============================================
      shipping: {
      /**
       * 배송 요청
       */
      request: async (packageIds, options = {}) => {
        return await useApiStore.request('/shipping/request', {
          method: 'POST',
          body: {
            customerEmail: window.customerEmail,
            warehouseId: options.warehouseId,
            packageIds,
            ...options
          },
          key: 'shipping.request'
        });
      },

      /**
       * 배송비 계산
       */
      calculate: async (params) => {
        return await useApiStore.request('/shipping/calculate', {
          method: 'POST',
          body: params,
          key: 'shipping.calculate'
        });
      },

      /**
       * 배송 방법 조회
       */
      getMethods: async (destination) => {
        return await useApiStore.request(`/shipping/methods?destination=${destination}`, {
          method: 'GET',
          key: 'shipping.methods'
        });
      }
    },

      // ============================================
      // 대시보드 API
      // ============================================
      dashboard: {
      /**
       * 대시보드 통계
       */
      getStats: async (customerEmail, customerId, warehouseId) => {
        return await useApiStore.request('/orders/stats', {
          method: 'POST',
          body: { customerEmail, customerId, warehouseId },
          key: 'dashboard.stats'
        });
      },

      /**
       * 대시보드 주문 목록
       */
      getOrders: async (customerId, customerEmail) => {
        return await useApiStore.request('/dashboard/orders', {
          method: 'POST',
          body: { customerId, customerEmail },
          key: 'dashboard.orders'
        });
      }
    },

      // ============================================
      // 고객 코드 API
      // ============================================
      customerCode: {
      /**
       * 고객 코드 생성
       */
      generate: async (customerId) => {
        return await useApiStore.request('/customer-code/generate', {
          method: 'POST',
          body: { customerId },
          key: 'customerCode.generate'
        });
      },

      /**
       * 고객 코드 검증
       */
      validate: async (code) => {
        return await useApiStore.request('/customer-code/validate', {
          method: 'POST',
          body: { code },
          key: 'customerCode.validate'
        });
      }
    },

      // ============================================
      // 유틸리티 메서드
      // ============================================
      
      /**
       * 로딩 상태 확인
       */
      isLoading: (key) => {
        const state = getState();
        return state.loading[key] || false;
      },

      /**
       * 에러 확인
       */
      getError: (key) => {
        const state = getState();
        return state.errors[key] || null;
      },

      /**
       * 모든 로딩 상태 초기화
       */
      clearLoading: () => {
        set({ loading: {} });
      },

      /**
       * 모든 에러 초기화
       */
      clearErrors: () => {
        set({ errors: {} });
      }
    };
  });

  // 전역으로 export
  window.Hallyu4UAPI = useApiStore;
  window.useApiStore = () => useApiStore.getState();

})();

